import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';

import { JsonHttpService } from 'src/app/shared/http/json-http.service';
import { FileHttpService } from 'src/app/shared/http/file-http.service';

import { ArtifactAssemblyInfoDto, ArtifactType, ArtifactXmlInfo, AssemblyArtifact, AssemblyArtifactDto, XmlArtifact } from '../models/artifact';
import { DownloaderService } from '../../shared/download/downloader.service';
import { NugetFeedPackage } from '../models/nuget-feed-package';
import { NugetFeed } from '../models/nuget-feed';
import { NugetPackage } from '../models/nuget-package';
import { NugetPackageContent } from '../models/nuget-package-content';

@Injectable({
    providedIn: 'root'
})
export class DeveloperArtifactService {

    constructor(
        private fileHttpService: FileHttpService,
        private httpService: JsonHttpService,
        private downloaderService: DownloaderService) { }

    /** Assembly Artifacts */

    getAssemblyArtifact(artifactId: string): Promise<AssemblyArtifact> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        return this.httpService.get('/api/developer/artifacts/get-assembly-artifact', options).toPromise();
    }

    getAssemblyArtifacts(includeSystemArtifacts: boolean, assemblyTypes?: string[]): Promise<AssemblyArtifactDto[]> {
        const options = { params: new HttpParams().set('includeSystemArtifacts', includeSystemArtifacts) };
        return firstValueFrom(this.httpService.postTyped<AssemblyArtifactDto[]>('/api/developer/artifacts/get-assembly-artifacts', assemblyTypes, options));
    }

    getAssemblyArtifactsIncludingInternal(assemblyTypes?: string[]): Promise<AssemblyArtifactDto[]> {
        const options = { params: new HttpParams().set('includeSystemArtifacts', true).set('includeInternalArtifacts', true) };
        return firstValueFrom(this.httpService.postTyped<AssemblyArtifactDto[]>('/api/developer/artifacts/get-assembly-artifacts', assemblyTypes, options));
    }

    resolveAssemblyArtifactTypes(fileUploadGuid: string): Promise<ArtifactAssemblyInfoDto[]> {
        const options = { params: new HttpParams().set('fileUploadGuid', fileUploadGuid) };
        return this.httpService.get('api/developer/artifacts/resolve-assembly-artifact-types', options).toPromise();
    }

    resolveMultipleAssemblyArtifactTypes(fileUploadGuids: string[]): Promise<AssemblyArtifactDto[]> {
        return firstValueFrom(this.httpService.postTyped<AssemblyArtifactDto[]>('api/developer/artifacts/resolve-multiple-assembly-artifact-types', fileUploadGuids));
    }

    async saveAssemblyArtifacts(artifacts: AssemblyArtifact[]): Promise<AssemblyArtifact[]> {
        const results: AssemblyArtifact[] = [];
        for (const a of artifacts) { // QUICK FIX: Kør dem sekventielt, da vi eller ser en deadlock i databasen.
            // eslint-disable-next-line no-await-in-loop
            results.push(await this.saveAssemblyArtifact(a));
        }
        return results;
    }

    saveAssemblyArtifact(artifact: AssemblyArtifact): Promise<AssemblyArtifact> {
        return this.httpService.post('/api/developer/artifacts/save-assembly-artifact', artifact).toPromise();
    }

    deleteAssemblyArtifact(artifactId: string, useGenericErrorHandling: (err: any, caught: Observable<any>) => boolean = () => true): Promise<void> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        return this.httpService.postWithErrorEval('api/developer/artifacts/delete-assembly-artifact', null, options, useGenericErrorHandling).toPromise();
    }

    getAssemblyArtifactDependencies(artifactId: string): Promise<AssemblyArtifact[]> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        return this.httpService.get('api/developer/artifacts/get-assembly-artifact-dependencies', options).toPromise();
    }

    async getMultipleAssemblyArtifactDependencies(artifactIds: string[]): Promise<AssemblyArtifact[]> {
        if (artifactIds.length === 0) {
            return Promise.resolve([] as AssemblyArtifact[]);
        }
        const options = { params: new HttpParams() };
        return this.httpService.post('api/developer/artifacts/get-multiple-assembly-artifact-dependencies', artifactIds, options).toPromise();
    }

    async getDocumentConfigAssemblyArtifacts(documentConfigId: number): Promise<AssemblyArtifact[]> {
        const options = { params: new HttpParams().set('documentConfigId', documentConfigId) };
        return this.httpService.get('api/developer/artifacts/get-document-config-assembly-artifacts', options).toPromise();
    }

    /** Xml Artifacts */

    getXmlArtifacts(includeSystemArtifacts: boolean, xmlTypes?: string[]): Promise<XmlArtifact[]> {
        const options = { params: new HttpParams().set('includeSystemArtifacts', includeSystemArtifacts) };
        return firstValueFrom(this.httpService.postTyped<XmlArtifact[]>('/api/developer/artifacts/get-xml-artifacts', xmlTypes, options));
    }

    resolveMultipleXmlArtifactTypes(fileUploadGuids: string[]): Promise<XmlArtifact[]> {
        return firstValueFrom(this.httpService.postTyped<XmlArtifact[]>('api/developer/artifacts/resolve-multiple-xml-artifact-types', fileUploadGuids));
    }

    resolveXmlArtifactTypes(fileUploadGuid: string): Promise<ArtifactXmlInfo> {
        const options = { params: new HttpParams().set('fileUploadGuid', fileUploadGuid) };
        return this.httpService.get('api/developer/artifacts/resolve-xml-artifact-types', options).toPromise();
    }

    async saveXmlArtifacts(artifacts: XmlArtifact[]): Promise<XmlArtifact[]> {
        const results: XmlArtifact[] = [];
        for (const a of artifacts) { // QUICK FIX: Kør dem sekventielt, da vi eller ser en deadlock i databasen.
            // eslint-disable-next-line no-await-in-loop
            results.push(await this.saveXmlArtifact(a));
        }
        return results;
    }

    saveXmlArtifact(artifact: XmlArtifact): Promise<XmlArtifact> {
        return this.httpService.post('/api/developer/artifacts/save-xml-artifact', artifact).toPromise();
    }

    deleteXmlArtifact(artifactId: string, useGenericErrorHandling: (err: any, caught: Observable<any>) => boolean = () => true): Promise<void> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        return this.httpService.postWithErrorEval('api/developer/artifacts/delete-xml-artifact', null, options, useGenericErrorHandling).toPromise();
    }

    allowDeletingXmlArtifact(artifactId: string): Promise<boolean> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        return this.httpService.post('api/developer/artifacts/allow-delete-xml-artifact', null, options).toPromise();
    }

    getXmlArtifactDependencies(artifactId: string): Promise<XmlArtifact[]> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        return this.httpService.get('api/developer/artifacts/get-xml-artifact-dependencies', options).toPromise();
    }

    getMultipleXmlArtifactDependencies(artifactIds: string[]): Promise<XmlArtifact[]> {
        if (artifactIds.length === 0) {
            return Promise.resolve([] as XmlArtifact[]);
        }
        const options = { params: new HttpParams() };
        return this.httpService.post('api/developer/artifacts/get-multiple-xml-artifact-dependencies', artifactIds, options).toPromise();
    }

    async getDocumentConfigXmlArtifacts(documentConfigId: number): Promise<XmlArtifact[]> {
        const options = { params: new HttpParams().set('documentConfigId', documentConfigId) };
        return this.httpService.get('api/developer/artifacts/get-document-config-xml-artifacts', options).toPromise();
    }

    /** Shared / Misc */
    async uploadArtifacts(files: File[]): Promise<string[]> {
        const uploadPromises = files.map(
            file => this.fileHttpService.upload<string>('api/developer/artifacts/upload-artifact-data', [file]));
        const fileGuids = await Promise.all(uploadPromises);
        return fileGuids;
    }

    async downloadArtifact(artifactId: string, artifactType: ArtifactType): Promise<void> {
        const options = { params: new HttpParams().set('artifactId', artifactId).set('artifactType', artifactType.toString()) };
        const token = await firstValueFrom(this.httpService.getTyped<string>('api/developer/artifacts/get-artifact-download-token', options));
        await this.downloaderService.downloadFile(token);
    }

    /** Nuget feed Artifacts */

    getNugetFeedInfo(nugetFeedId: number, includePrerelease: boolean, searchTerm: string): Promise<NugetFeedPackage[]> {
        const options = {
            params: new HttpParams().set('nugetFeedId', nugetFeedId)
                .set('includePrerelease', includePrerelease)
                .set('searchTerm', searchTerm)
        };
        return this.httpService.get('/api/developer/artifacts/get-nuget-feed-info', options, false).toPromise();
    }

    getNugetPackages(packages: NugetPackage[]): Promise<NugetPackageContent[]> {
        return this.httpService.post('/api/developer/artifacts/get-nuget-packages', packages).toPromise();
    }

    getNugetFeeds(): Promise<NugetFeed[]> {
        return this.httpService.get('/api/developer/artifacts/get-nuget-feeds').toPromise();
    }

    saveNugetFeed(nugetFeed: NugetFeed, passwordChanged: boolean): Promise<NugetFeed> {
        const options = { params: new HttpParams().set('passwordChanged', passwordChanged) };
        return this.httpService.post('/api/developer/artifacts/save-nuget-feed', nugetFeed, options).toPromise();
    }

    deleteNugetFeed(nugetFeedId: number): Promise<void> {
        const options = { params: new HttpParams().set('nugetFeedId', nugetFeedId.toString()) };
        return this.httpService.post('/api/developer/artifacts/delete-nuget-feed', null, options).toPromise();
    }
}
